/**
 * Presentation rules for the lead qualification score.
 *
 * The score itself lives in `@/types/crm` (`qualificationScore`) because the
 * import scripts compute it too; only the colours and labels are here.
 */
import type { Qualification } from '@/types/crm'

export interface ScoreStyle {
  label: string
  color: string
  bg: string
}

/**
 * Indexed by score, 0–3. Deliberately not a gradient: a rep scanning the table
 * needs "worth my morning" to read differently from "worth a look", and three
 * shades of the same hue does not do that.
 */
export const SCORE_STYLES: Record<number, ScoreStyle> = {
  3: { label: 'High priority', color: '#3aa52f', bg: '#eaf7e7' },
  2: { label: 'Qualified', color: '#3456da', bg: '#eaefff' },
  1: { label: 'Low priority', color: '#e8a52f', bg: '#fff3d8' },
  0: { label: 'Not qualified', color: '#aeb1b4', bg: '#f1f2f3' },
}

export function scoreStyle(score: number): ScoreStyle {
  return SCORE_STYLES[score] ?? SCORE_STYLES[0]
}

/** The three criteria, in the order they are shown everywhere. */
export const QUALIFICATION_CRITERIA: {
  key: keyof Omit<Qualification, 'comment'>
  label: string
  hint: string
}[] = [
  {
    key: 'hasVolume',
    label: 'Size & volume',
    hint: 'Enough guides for a B2B deal',
  },
  {
    key: 'hasDigitalTools',
    label: 'Digital maturity',
    hint: 'Already uses apps/maps/tracking',
  },
  {
    key: 'highSpend',
    label: 'Purchasing power',
    hint: 'High-cost segment',
  },
]
